/**
 * Crisp live chat for the Atlas handoff, on the warbler side (AS-11647).
 *
 * Kept free of Vue, EventBus and the store so mocha can load it: App.vue and
 * AtlasEmbed.vue cannot be mounted under vue-shim, so the behaviour lives here
 * and the components only call it.
 *
 * Two jobs:
 *  - open the widget for a handoff and say honestly whether it opened (P0-4);
 *  - keep one record of what the widget is doing (open, unread, handoff) for
 *    the Atlas iframe and the agent FAB (P0-5).
 */
import type { HandoffIntent, LiveChatState, OpenLiveChatPayload } from '@/services/atlasIframeService';
import { deliverHandoffContext, handoffCancellations, handoffContextDelivered } from '@/services/crispDelivery';
import type { CrispSdkLike } from '@/services/crispDelivery';

export interface CrispWindowLike {
  $crisp?: { push?: (command: unknown[]) => void; is?: unknown } | unknown[];
}

export interface HandoffResult {
  success: boolean;
  message: string;
}

export const LIVE_CHAT_OPENING = 'Live chat opening.';
export const LIVE_CHAT_UNAVAILABLE = 'Live chat is not available right now.';
/** Opened, but Crisp has not confirmed the summary yet: it was idle and is
 *  reconnecting. Delivery carries on (crispDelivery.ts); the merchant is told. */
export const LIVE_CHAT_RECONNECTING =
  'Live chat is open but still reconnecting. If it asks to reconnect, click it — your summary is sent as soon as it is back.';
/** How long the answer to Atlas waits for Crisp to confirm the context: an
 *  idle socket takes 2-4 s to reconnect, so as long as Atlas's 3 s bridge
 *  timeout allows — less after the boot retry above, which spent 1.5 s. */
export const DELIVERY_REPORT_MS = 2500;
export const DELIVERY_REPORT_AFTER_RETRY_MS = 1000;

/** One retry for an SDK that is still booting — the same budget atlas gives a
 *  boot-time load (SupportTools.tsx). Atlas waits 3 s for the answer, so this
 *  must stay well under that. */
export const CRISP_READY_RETRY_MS = 1500;

/**
 * Is the Crisp SDK live? l.js swaps the `$crisp = []` queue for an SDK object
 * with `.is()` once it loads. A queue that an ad-blocker kept l.js from ever
 * draining is NOT live, though `$crisp` exists — the case that used to be
 * reported to the merchant as "connecting you".
 */
export function isCrispReady(win: CrispWindowLike): boolean {
  const crisp = win.$crisp as { is?: unknown } | undefined;
  return !!crisp && typeof crisp.is === 'function';
}

function push(win: CrispWindowLike, command: unknown[]): void {
  (win.$crisp as { push: (c: unknown[]) => void }).push(command);
}

/** Atlas's summary, as the first line the operator reads in the conversation. */
export function summaryMessage(summary: string): string {
  return `Summary from Atlas (AI assistant): ${summary}`;
}

/**
 * Open Crisp for an Atlas handoff and report whether it really opened.
 * `wait` is injected so tests do not sleep.
 */
export async function openCrispForHandoff(
  win: CrispWindowLike,
  payload: OpenLiveChatPayload,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<HandoffResult> {
  const retried = !isCrispReady(win);
  if (retried) {
    const cancellations = handoffCancellations();
    await wait(CRISP_READY_RETRY_MS);
    // Logged out (or evicted) while we waited: this merchant's summary and
    // shop must not reach the next person's Crisp session.
    if (handoffCancellations() !== cancellations) return { success: false, message: LIVE_CHAT_UNAVAILABLE };
    if (!isCrispReady(win)) return { success: false, message: LIVE_CHAT_UNAVAILABLE };
  }
  push(win, ['do', 'chat:show']);
  push(win, ['do', 'chat:open']);
  // Keys match atlas's standalone handoff (frontend/src/config/crisp.ts)
  // verbatim, so support sees one record whichever shell the merchant is in.
  const messages = [
    ...(payload.summary ? [summaryMessage(payload.summary)] : []),
    // AS-10883: the downgrade funnel's own text.
    ...(payload.message ? [payload.message] : []),
  ];
  deliverHandoffContext(win.$crisp as CrispSdkLike, [
    ['ai_handoff', 'true'],
    ['source', 'atlas-agent'],
    ...(payload.handoffIntent ? [['handoff_intent', payload.handoffIntent]] : []),
    ...(payload.ticketKey ? [['ticket_key', payload.ticketKey]] : []),
    ...(payload.reason ? [['handoff_reason', payload.reason]] : []),
    ...(payload.shop ? [['shop', payload.shop]] : []),
  ], messages, () => nudgeCrisp(win));
  liveChatState.markHandoff();
  liveChatState.noteOpened();
  const delivered = await handoffContextDelivered(retried ? DELIVERY_REPORT_AFTER_RETRY_MS : DELIVERY_REPORT_MS);
  return { success: true, message: delivered ? LIVE_CHAT_OPENING : LIVE_CHAT_RECONNECTING };
}

/** Crisp's activity listener on warbler's window: reconnects a socket it
 *  dropped as idle. Typing to Atlas happens in the iframe and never reaches it. */
function nudgeCrisp(win: CrispWindowLike): void {
  const target = win as unknown as { dispatchEvent?: (e: Event) => boolean };
  if (typeof target.dispatchEvent === 'function' && typeof Event === 'function') {
    target.dispatchEvent(new Event('mousemove'));
  }
}

/** Bring back a handoff conversation the merchant minimised. No session data. */
export function reopenCrisp(win: CrispWindowLike): void {
  if (!isCrispReady(win)) return;
  push(win, ['do', 'chat:show']);
  push(win, ['do', 'chat:open']);
  liveChatState.noteOpened();
}

/** Crisp's open chatbox panel (its own markup; no stable id). */
export const CRISP_PANEL_SELECTOR = '#crisp-chatbox [data-chat-status][data-visible="true"]';
export const PILL_HEIGHT_PX = 28;
export const PILL_GAP_PX = 8;

/**
 * Where the "Atlas agent" pill sits: just above Crisp's chatbox, left-aligned
 * with it, as in the design (AS-11647 option A). Crisp's panel moves with the
 * viewport and its own animation, so callers measure it while the chat is
 * open. Null when it cannot be measured: the stylesheet position applies.
 */
export function crispPanelAnchor(doc: { querySelector: (s: string) => Element | null }): { left: number; top: number } | null {
  const rect = doc.querySelector(CRISP_PANEL_SELECTOR)?.getBoundingClientRect();
  if (!rect || rect.width <= 0 || rect.top <= PILL_HEIGHT_PX + PILL_GAP_PX) return null;
  return { left: Math.round(rect.left), top: Math.round(rect.top - PILL_HEIGHT_PX - PILL_GAP_PX) };
}

/** Close the chatbox — the "Atlas agent" pill. App.vue's chat:closed handler hides it. */
export function closeCrisp(win: CrispWindowLike): void {
  if (!isCrispReady(win)) return;
  push(win, ['do', 'chat:close']);
}

export type { HandoffIntent };

type Listener = (state: LiveChatState) => void;

/** Per tab, like Crisp's own session: a reload must not lose the way back. */
export const LIVE_CHAT_STORAGE_KEY = 'assisty_live_chat_handoff';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserSession(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * What the Crisp widget is doing, fed by App.vue's Crisp event handlers.
 * A module singleton because the widget itself is one per page.
 *
 * `handoff` and `unread` survive a reload (sessionStorage): without them the
 * Atlas way back and the unread dot vanished on every refresh while the
 * conversation itself carried on in Crisp. `open` is Crisp's to report again.
 */
class LiveChatStateStore {
  private state: LiveChatState = { open: false, unread: false, handoff: false };
  private listeners = new Set<Listener>();

  constructor(private readonly storage: () => StorageLike | null = browserSession) {
    this.restore();
  }

  get current(): LiveChatState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Crisp `chat:opened`, and our own chat:open: Crisp restores a chatbox left
   * open on the last page load as already opened (App.vue hides it at boot),
   * so opening it again fires no chat:opened. Whatever was unread is being
   * read now.
   */
  noteOpened(): void {
    this.set({ open: true, unread: false });
  }

  /** Crisp `chat:closed`. */
  noteClosed(): void {
    this.set({ open: false });
  }

  /** Crisp `message:received`: news only while the chatbox is closed. */
  noteMessageReceived(): void {
    if (!this.state.open) this.set({ unread: true });
  }

  /** A handoff from Atlas opened the widget this session. */
  markHandoff(): void {
    this.set({ handoff: true });
  }

  /** Crisp `session:loaded`: replies that arrived while the page was away. */
  noteUnreadCount(count: number): void {
    if (count > 0 && !this.state.open) this.set({ unread: true });
  }

  /** Logout: the next person in this tab must not inherit the way back. */
  forget(): void {
    this.set({ open: false, unread: false, handoff: false });
  }

  /** Tests only. */
  reset(): void {
    this.state = { open: false, unread: false, handoff: false };
    this.listeners.clear();
  }

  /** Tests only: re-read storage, as a fresh page load would. */
  restore(): void {
    try {
      const raw = this.storage()?.getItem(LIVE_CHAT_STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (saved && typeof saved === 'object') {
        this.state = { open: false, unread: saved.unread === true, handoff: saved.handoff === true };
      }
    } catch {
      /* unreadable: start closed */
    }
  }

  private persist(): void {
    try {
      const storage = this.storage();
      if (!storage) return;
      if (this.state.handoff || this.state.unread) {
        storage.setItem(LIVE_CHAT_STORAGE_KEY, JSON.stringify({ handoff: this.state.handoff, unread: this.state.unread }));
      } else {
        storage.removeItem(LIVE_CHAT_STORAGE_KEY);
      }
    } catch {
      /* storage full or blocked: the in-memory state still works */
    }
  }

  private set(patch: Partial<LiveChatState>): void {
    const next = { ...this.state, ...patch };
    if (next.open === this.state.open && next.unread === this.state.unread && next.handoff === this.state.handoff) {
      return;
    }
    this.state = next;
    this.persist();
    this.listeners.forEach((l) => l(next));
  }
}

export const liveChatState = new LiveChatStateStore();

/** Tests only. */
export function createLiveChatState(storage: () => StorageLike | null): LiveChatStateStore {
  return new LiveChatStateStore(storage);
}
